import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { GoalWithStatus } from '../../service/api';
import Goal from '../Goal';
import GoalDetailsModal from '../GoalDetailsModal';

interface GoalsSectionProps {
  goals: GoalWithStatus[];
  isLoading: boolean;
  error: string | null;
}

const GoalsSection: React.FC<GoalsSectionProps> = ({
  goals,
  isLoading,
  error,
}) => {
  const [selectedGoal, setSelectedGoal] = useState<GoalWithStatus | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();

  const handleGoalClick = (goalData: GoalWithStatus) => {
    setSelectedGoal(goalData);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedGoal(null);
  };

  if (isLoading) {
    return (
      <div
        className="text-center py-5 rounded-3 mb-4"
        style={{ backgroundColor: 'rgba(6, 56, 96, 0.85)' }}
      >
        <Spinner animation="border" variant="light" size="sm" />
        <p className="mt-3 text-white-50 mb-0">{t('goals.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="p-4 rounded-3 mb-4 text-center"
        style={{
          backgroundColor: 'rgba(220, 53, 69, 0.2)',
          border: '1px solid rgba(220, 53, 69, 0.3)',
        }}
      >
        <div className="text-danger fs-5 fw-bold mb-2">
          {t('goals.errorLoading')}
        </div>
        <div className="text-white-50">{t('goals.failedToLoad')}</div>
      </div>
    );
  }

  if (!goals.length) {
    return (
      <div
        className="text-center py-5 rounded-3 mb-4"
        style={{ backgroundColor: 'rgba(6, 56, 96, 0.85)' }}
      >
        <div className="text-white-50 fs-5">{t('goals.notFound')}</div>
      </div>
    );
  }

  const activeGoals = goals.filter(
    g => !g.status.isCompleted || g.goal.config.repeatable,
  );
  const completedGoals = goals.filter(
    g => g.status.isCompleted && !g.goal.config.repeatable,
  );

  return (
    <div className="mb-4">
      {activeGoals.length > 0 && (
        <div className="mb-4">
          <h2 className="mb-4 text-white fw-bold fs-1">
            {t('goals.activeGoals')}
          </h2>
          {activeGoals.map(goalData => (
            <Goal
              key={goalData.goal._id}
              goalData={goalData}
              onClick={() => handleGoalClick(goalData)}
            />
          ))}
        </div>
      )}

      {completedGoals.length > 0 && (
        <div className="mb-4">
          <h3 className="mb-4 text-white-50 fw-bold fs-2">
            {t('goals.completedGoals')}
          </h3>
          <div style={{ opacity: 0.7 }}>
            {completedGoals.map(goalData => (
              <Goal
                key={goalData.goal._id}
                goalData={goalData}
                onClick={() => handleGoalClick(goalData)}
              />
            ))}
          </div>
        </div>
      )}

      <GoalDetailsModal
        show={showModal}
        onHide={handleCloseModal}
        goalData={selectedGoal}
      />
    </div>
  );
};

export default GoalsSection;
